import {
  ApiPathStatus,
  ApiProject,
  ApiReadLanderResponse,
  ApiSyncType,
  ApiUTMEntityLevel,
} from "@incendium/api";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import EntityTypeChip from "features/rezonate/components/EntityTypeChip";
import { useMetaUtmEntites } from "features/dataConnectors/hooks/useMetaUtmEntites";
import { updateSmartLinkEntities } from "features/rezonate/utils/updateSmartLinkEntities";
import { buildFlatEntityList } from "features/rezonate/utils/buildEntityHierarchy";
import { updateSmartLink } from "features/rezonate/services/smartLinkService";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import { useNotification } from "Hooks";
import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { EntityType } from "features/rezonate/types";

interface SmartLinksSectionProps {
  project: ApiProject;
  lander: ApiReadLanderResponse;
}

interface EntityOption {
  id: number;
  name: string;
  level: ApiUTMEntityLevel;
  type: EntityType;
  campaignName?: string;
  adGroupName?: string;
  label: string;
  campaignId?: number;
  adGroupId?: number;
}

function SmartLinksSection({ project, lander }: SmartLinksSectionProps) {
  const { entites, loading, setEntites } = useMetaUtmEntites();
  const { externalSyncs } = useExternalSyncs();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [selectedEntities, setSelectedEntities] = useState<EntityOption[]>([]);
  const [saving, setSaving] = useState(false);
  const previouslyLinkedEntitiesRef = useRef<EntityOption[]>([]);

  const metaSync = useMemo(() => {
    return externalSyncs.find((es) => es.type === ApiSyncType.FACEBOOK_ADS);
  }, [externalSyncs]);

  // Build options from entities, nested hierarchically: campaigns -> ad groups -> ads
  const entityOptions = useMemo(() => {
    const flatList = buildFlatEntityList(entites);
    // Add label property for compatibility with existing code
    return flatList.map((entity) => ({
      id: entity.id,
      name: entity.name,
      level: entity.level,
      type: entity.type,
      campaignName: entity.campaignName,
      adGroupName: entity.adGroupName,
      campaignId: entity.campaignId,
      adGroupId: entity.adGroupId,
      label: entity.name,
    })) as EntityOption[];
  }, [entites]);

  // Load existing smart links on mount
  useEffect(() => {
    if (!metaSync || !lander.id || loading || entityOptions.length === 0) {
      return;
    }

    // Find entities that are already linked to this lander
    // Check campaigns, ad groups, and ads
    const linkedEntities: EntityOption[] = [];
    const seenIds = new Set<string>();

    entites.forEach((entity) => {
      // Check if campaign is linked to this lander
      if (entity.externalCampaign?.landingPathId === lander.id) {
        const campaignOption = entityOptions.find(
          (opt) => opt.type === "campaign" && opt.id === entity.externalCampaign?.id
        );
        if (campaignOption) {
          const key = `${campaignOption.type}-${campaignOption.id}`;
          if (!seenIds.has(key)) {
            seenIds.add(key);
            linkedEntities.push(campaignOption);
          }
        }
      }

      // Check if ad group is linked to this lander
      if (entity.externalAdgroup?.landingPathId === lander.id) {
        const adGroupOption = entityOptions.find(
          (opt) => opt.type === "adGroup" && opt.id === entity.externalAdgroup?.id
        );
        if (adGroupOption) {
          const key = `${adGroupOption.type}-${adGroupOption.id}`;
          if (!seenIds.has(key)) {
            seenIds.add(key);
            linkedEntities.push(adGroupOption);
          }
        }
      }

      // Check if ad is linked to this lander
      if (entity.landingPathId === lander.id) {
        const adOption = entityOptions.find(
          (opt) => opt.type === "ad" && opt.id === entity.id
        );
        if (adOption) {
          const key = `${adOption.type}-${adOption.id}`;
          if (!seenIds.has(key)) {
            seenIds.add(key);
            linkedEntities.push(adOption);
          }
        }
      }
    });

    setSelectedEntities(linkedEntities);
    previouslyLinkedEntitiesRef.current = linkedEntities;
  }, [metaSync, lander.id, loading, entityOptions, entites]);

  const handleSave = useCallback(async () => {
    if (!metaSync || !lander.id) {
      showErrorNotification("Facebook Ads sync not connected or lander not found");
      return;
    }

    setSaving(true);
    try {
      const previouslyLinked = previouslyLinkedEntitiesRef.current;

      // Helper function to create a unique key for an entity
      const getEntityKey = (entity: EntityOption) => `${entity.level}-${entity.id}`;

      // Find entities that were previously linked but are no longer selected (need to remove)
      const entitiesToRemove = previouslyLinked.filter(
        (prevEntity) =>
          !selectedEntities.some(
            (selectedEntity) => getEntityKey(selectedEntity) === getEntityKey(prevEntity)
          )
      );

      // Remove links for entities that are no longer selected
      const removePromises = entitiesToRemove.map((entity) =>
        updateSmartLink(project.id as number, entity.level, entity.id, null, undefined)
      );

      // Save links for currently selected entities
      const addPromises = selectedEntities.map((entity) =>
        updateSmartLink(project.id as number, entity.level, entity.id, lander.id, undefined)
      );

      // Execute all updates in parallel
      await Promise.all([...removePromises, ...addPromises]);

      // Update entities in shared state to reflect the changes
      setEntites((prev) => {
        let updated = prev;

        // Remove links for entities that are no longer selected
        entitiesToRemove.forEach((entity) => {
          updated = updateSmartLinkEntities(
            updated,
            entity.level,
            entity.id,
            undefined, // Remove landingPathId
            undefined  // Remove rezonateUrl
          );
        });

        // Add links for currently selected entities
        selectedEntities.forEach((entity) => {
          updated = updateSmartLinkEntities(
            updated,
            entity.level,
            entity.id,
            lander.id, // Set landingPathId
            undefined   // No rezonateUrl when linking to lander
          );
        });

        return updated;
      });

      // Update the ref to reflect the new state
      previouslyLinkedEntitiesRef.current = selectedEntities;

      showSuccessNotification("Smart links updated successfully");
    } catch (error) {
      showErrorNotification("Failed to update smart links");
    } finally {
      setSaving(false);
    }
  }, [selectedEntities, metaSync, lander, project, showSuccessNotification, showErrorNotification, setEntites]);

  if (!metaSync) {
    return (
      <Alert severity="info">
        <Typography variant="body2">
          Connect a Facebook Ads account to enable smart links.
        </Typography>
      </Alert>
    );
  }

  if (!lander.id) {
    return (
      <Alert severity="warning">
        <Typography variant="body2">
          Please save the General Settings (subdomain and path) first to enable smart links.
          The lander must be created before you can link it to ads, ad sets, or campaigns.
        </Typography>
      </Alert>
    );
  }

  const isLanderActive = lander.status === ApiPathStatus.PATH_STATUS_ACTIVE;

  return (
    <Stack spacing={3}>
      {!isLanderActive && (
        <Alert severity="warning">
          <Typography variant="body2">
            <strong>Warning:</strong> This lander is not active. Ads linked to this lander may point to a dead end.
            Please activate the lander before linking it to ads, ad sets, or campaigns.
          </Typography>
        </Alert>
      )}
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <Typography variant="subtitle2">Link to Meta Ads/Groups/Campaigns</Typography>
          <Tooltip
            title={
              <Box>
                <Typography variant="body2" mb={1} color="inherit">
                  <strong>Cascading Behavior:</strong>
                </Typography>
                <Typography variant="body2" component="div" color="inherit">
                  • <strong>Campaign:</strong> Applies to all ad sets and ads in the campaign
                </Typography>
                <Typography variant="body2" component="div" color="inherit">
                  • <strong>Ad Set:</strong> Applies to all ads in the set (unless ad has specific link)
                </Typography>
                <Typography variant="body2" component="div" color="inherit">
                  • <strong>Ad:</strong> Specific link for this ad only
                </Typography>
                <Typography variant="body2" mt={1} color="inherit">
                  You can link this lightning lander to multiple ads, ad sets, or campaigns.
                </Typography>
              </Box>
            }
            arrow
            placement="top"
          >
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Autocomplete
          multiple
          options={entityOptions}
          getOptionLabel={(option) => option.name}
          value={selectedEntities}
          onChange={(_, newValue) => setSelectedEntities(newValue)}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select ads, ad sets, or campaigns"
              size="small"
            />
          )}
          renderOption={(props, option) => {
            const getIndent = () => {
              if (option.type === "campaign") return 0;
              if (option.type === "adGroup") return 12;
              if (option.type === "ad") return 24;
              return 0;
            };

            return (
              <Box
                component="li"
                {...props}
                sx={{
                  pl: `${getIndent()}px !important`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <EntityTypeChip
                  type={option.type}
                  sx={{ minWidth: 80 }}
                  color={option.type === "campaign" ? "default" : undefined}
                />
                <Typography variant="body2">{option.name}</Typography>
                {option.campaignName && option.type === "ad" && (
                  <Typography variant="caption" color="text.secondary">
                    ({option.adGroupName} • {option.campaignName})
                  </Typography>
                )}
                {option.campaignName && option.type === "adGroup" && (
                  <Typography variant="caption" color="text.secondary">
                    ({option.campaignName})
                  </Typography>
                )}
              </Box>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <EntityTypeChip
                {...getTagProps({ index })}
                key={`${option.type}-${option.id}`}
                type={option.type}
                label={option.name}
              />
            ))
          }
          isOptionEqualToValue={(option, value) =>
            option.id === value.id && option.level === value.level
          }
        />
      </Box>

      {selectedEntities.length > 0 && (
        <Alert severity="info">
          <Typography variant="body2">
            This lander will be linked to {selectedEntities.length}
            {selectedEntities.length > 1 ? " entities" : " entity"}. The cascading behavior
            will apply based on the entity type selected.
          </Typography>
        </Alert>
      )}

      <Box>
        <Typography variant="body2" color="text.secondary">
          Selected entities will use this lander as their smart link destination.
          Lower-level entities (ads) will override higher-level settings (campaigns/ad sets).
        </Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        disabled={saving || !lander.id}
        fullWidth
      >
        {saving ? "Saving..." : "Save Smart Links"}
      </Button>
    </Stack>
  );
}

export default SmartLinksSection;

